import { useTranslator } from "@eo-locale/react";

import { array } from "yup";

import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  CheckboxGroup,
  Checkbox,
  Wrap,
  WrapItem,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface CheckboxQuestionProps {
  question: Question;
}

export const validate = async (
  answer: string[],
  question: Question
): Promise<string[]> => {
  let schema = array().nullable();
  if (question.required) {
    schema = schema.required();
  }
  if (question.checkboxAnswersLimit) {
    schema = schema.max(question.checkboxAnswersLimit);
  }
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const CheckBoxQuestion = ({ question }: CheckboxQuestionProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || [];

  const handleChange = (value: string[]): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: value.length ? value.map(Number) : null
      })
    );
  };

  return (
    <FormControl
      my={4}
      as="fieldset"
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel as="legend">
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <CheckboxGroup
        onChange={handleChange}
        value={answer.map(String)}
        size={{ base: "lg", lg: "md" }}
      >
        <Wrap
          spacing={4}
          direction={{
            base: "column",
            lg: question.horizontal ? "row" : "column"
          }}
        >
          {[...question.questionOptions]
            .filter((option) => !option.deleted)
            .sort((a, b) => a.sequence - b.sequence)
            .map((option) => (
              <WrapItem key={option.id}>
                <Checkbox
                  pl={1}
                  value={`${option.id}`}
                  isInvalid={error.length > 0}
                  isDisabled={status === "saving"}
                  data-testid={`checkboxQuestion-${option.name}`}
                  name="checkboxQuestion-checkbox"
                >
                  {option.name}
                </Checkbox>
              </WrapItem>
            ))}
        </Wrap>
      </CheckboxGroup>
      {error.length > 0 && (
        <FormErrorMessage data-testid="checkboxQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default CheckBoxQuestion;
